'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface NetworkHealth {
  id: string;
  score: number;
  trend_delta: number;
  updated_at: string;
}

export interface KpiMetric {
  id: string;
  metric_key: string;
  label: string;
  value: string;
  sub_label: string;
  trend: string;
  trend_val: string;
  variant: string;
  updated_at: string;
}

export interface NetworkAlert {
  id: string;
  severity: 'p1' | 'p2' | 'p3';
  message: string;
  node_name: string;
  ai_confidence: number;
  correlated: boolean;
  dismissed: boolean;
  created_at: string;
}

export interface NetworkNode {
  id: string;
  node_key: string;
  name: string;
  region: string;
  node_type: string;
  node_status: 'healthy' | 'degraded' | 'critical' | 'recovering' | 'isolated';
  cpu: number;
  memory: number;
  uptime: string;
  bgp_peers: number;
  last_event: string;
  updated_at: string;
}

export interface RegionHealth {
  id: string;
  region_key: string;
  name: string;
  total_nodes: number;
  healthy_nodes: number;
  degraded_nodes: number;
  critical_nodes: number;
  incidents: number;
  throughput: string;
  latency: string;
  score: number;
  updated_at: string;
}

export interface ThroughputPoint {
  id: string;
  time_label: string;
  inbound: number;
  outbound: number;
  recorded_at: string;
}

export interface LatencyPoint {
  id: string;
  time_label: string;
  p50: number;
  p95: number;
  p99: number;
  recorded_at: string;
}

export interface DashboardData {
  health: NetworkHealth | null;
  kpis: KpiMetric[];
  alerts: NetworkAlert[];
  nodes: NetworkNode[];
  regions: RegionHealth[];
  throughput: ThroughputPoint[];
  latency: LatencyPoint[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  suppressCount: number;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useDashboardRealtime(): DashboardData {
  const supabase = createClient();
  const [health, setHealth] = useState<NetworkHealth | null>(null);
  const [kpis, setKpis] = useState<KpiMetric[]>([]);
  const [alerts, setAlerts] = useState<NetworkAlert[]>([]);
  const [nodes, setNodes] = useState<NetworkNode[]>([]);
  const [regions, setRegions] = useState<RegionHealth[]>([]);
  const [throughput, setThroughput] = useState<ThroughputPoint[]>([]);
  const [latency, setLatency] = useState<LatencyPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [suppressCount, setSuppressCount] = useState(142);
  const suppressRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const touch = useCallback(() => setLastUpdated(new Date()), []);

  // ── Initial fetch ──────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    try {
      const [
        { data: healthData },
        { data: kpiData },
        { data: alertData },
        { data: nodeData },
        { data: regionData },
        { data: throughputData },
        { data: latencyData },
      ] = await Promise.all([
        supabase.from('network_health').select('*').order('updated_at', { ascending: false }).limit(1).maybeSingle(),
        supabase.from('kpi_metrics').select('*').order('metric_key'),
        supabase.from('network_alerts').select('*').eq('dismissed', false).order('created_at', { ascending: false }).limit(20),
        supabase.from('network_nodes').select('*').order('name'),
        supabase.from('region_health').select('*').order('name'),
        supabase.from('throughput_series').select('*').order('recorded_at', { ascending: true }).limit(12),
        supabase.from('latency_series').select('*').order('recorded_at', { ascending: true }).limit(12),
      ]);

      if (healthData) setHealth(healthData as NetworkHealth);
      if (kpiData) setKpis(kpiData as KpiMetric[]);
      if (alertData) setAlerts(alertData as NetworkAlert[]);
      if (nodeData) setNodes(nodeData as NetworkNode[]);
      if (regionData) setRegions(regionData as RegionHealth[]);
      if (throughputData) setThroughput(throughputData as ThroughputPoint[]);
      if (latencyData) setLatency(latencyData as LatencyPoint[]);
      touch();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load telemetry';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [supabase, touch]);

  useEffect(() => {
    fetchAll();

    // ── Real-time subscriptions ──────────────────────────────────────────────

    const healthChannel = supabase
      .channel('rt_network_health')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'network_health' }, (payload) => {
        if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
          setHealth(payload.new as NetworkHealth);
          touch();
        }
      })
      .subscribe();

    const kpiChannel = supabase
      .channel('rt_kpi_metrics')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'kpi_metrics' }, (payload) => {
        if (payload.eventType === 'INSERT') {
          setKpis((prev) => [...prev, payload.new as KpiMetric]);
        } else if (payload.eventType === 'UPDATE') {
          setKpis((prev) =>
            prev.map((k) => (k.id === (payload.new as KpiMetric).id ? (payload.new as KpiMetric) : k))
          );
        } else if (payload.eventType === 'DELETE') {
          setKpis((prev) => prev.filter((k) => k.id !== (payload.old as KpiMetric).id));
        }
        touch();
      })
      .subscribe();

    const alertChannel = supabase
      .channel('rt_network_alerts')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'network_alerts' }, (payload) => {
        if (payload.eventType === 'INSERT') {
          const newAlert = payload.new as NetworkAlert;
          if (!newAlert.dismissed) {
            setAlerts((prev) => [newAlert, ...prev].slice(0, 20));
            setSuppressCount((c) => c + 1);
          }
        } else if (payload.eventType === 'UPDATE') {
          const updated = payload.new as NetworkAlert;
          if (updated.dismissed) {
            setAlerts((prev) => prev.filter((a) => a.id !== updated.id));
            setSuppressCount((c) => c + 1);
          } else {
            setAlerts((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
          }
        } else if (payload.eventType === 'DELETE') {
          setAlerts((prev) => prev.filter((a) => a.id !== (payload.old as NetworkAlert).id));
        }
        touch();
      })
      .subscribe();

    const nodeChannel = supabase
      .channel('rt_network_nodes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'network_nodes' }, (payload) => {
        if (payload.eventType === 'INSERT') {
          setNodes((prev) => [...prev, payload.new as NetworkNode]);
        } else if (payload.eventType === 'UPDATE') {
          setNodes((prev) =>
            prev.map((n) => (n.id === (payload.new as NetworkNode).id ? (payload.new as NetworkNode) : n))
          );
        } else if (payload.eventType === 'DELETE') {
          setNodes((prev) => prev.filter((n) => n.id !== (payload.old as NetworkNode).id));
        }
        touch();
      })
      .subscribe();

    const regionChannel = supabase
      .channel('rt_region_health')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'region_health' }, (payload) => {
        if (payload.eventType === 'INSERT') {
          setRegions((prev) => [...prev, payload.new as RegionHealth]);
        } else if (payload.eventType === 'UPDATE') {
          setRegions((prev) =>
            prev.map((r) => (r.id === (payload.new as RegionHealth).id ? (payload.new as RegionHealth) : r))
          );
        } else if (payload.eventType === 'DELETE') {
          setRegions((prev) => prev.filter((r) => r.id !== (payload.old as RegionHealth).id));
        }
        touch();
      })
      .subscribe();

    const throughputChannel = supabase
      .channel('rt_throughput_series')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'throughput_series' }, (payload) => {
        setThroughput((prev) => [...prev, payload.new as ThroughputPoint].slice(-12));
        touch();
      })
      .subscribe();

    const latencyChannel = supabase
      .channel('rt_latency_series')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'latency_series' }, (payload) => {
        setLatency((prev) => [...prev, payload.new as LatencyPoint].slice(-12));
        touch();
      })
      .subscribe();

    // Suppress counter ticks (simulates AI noise reduction)
    suppressRef.current = setInterval(() => {
      setSuppressCount((c) => c + Math.floor(Math.random() * 3));
    }, 5000);

    return () => {
      supabase.removeChannel(healthChannel);
      supabase.removeChannel(kpiChannel);
      supabase.removeChannel(alertChannel);
      supabase.removeChannel(nodeChannel);
      supabase.removeChannel(regionChannel);
      supabase.removeChannel(throughputChannel);
      supabase.removeChannel(latencyChannel);
      if (suppressRef.current) clearInterval(suppressRef.current);
    };
  }, [fetchAll, supabase, touch]);

  return {
    health,
    kpis,
    alerts,
    nodes,
    regions,
    throughput,
    latency,
    loading,
    error,
    lastUpdated,
    suppressCount,
  };
}
